import {AfterContentInit, AfterViewInit, Component, OnDestroy, OnInit, ViewContainerRef,} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig} from '@angular/material/legacy-dialog';
import {MatLegacySnackBar as MatSnackBar, MatLegacySnackBarConfig as MatSnackBarConfig} from '@angular/material/legacy-snack-bar';
import {ActivatedRoute, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {filter, takeWhile} from 'rxjs/operators';

import {ngcAnimations} from '@app/core/comp/animations';
import * as authAction from '../auth.actions';
import {getAuthenticatedUser, getAuthenticationError, State} from '@app/app.reducers';
import {User, UserStatus} from '@app/core/model/user';
import {ThemeService} from '@app/core/service/theme.service';
import {TokenService} from '@app/core/security/token.service';
import {isLoginRoute} from '@app/core/main.module.routes';
import {copyData} from '@app/core/model/integration-models';
import {AuthenticatedUserService} from '@app/core/service/authenticated-user.service';
import {ConfirmDialogComponent} from '@app/core/comp/dialog/confirm-dialog/confirm-dialog.component';
import {OidcSecurityProxyService} from '@app/core/service/oidc.security.proxy.service';
import {environment} from '@env/environment';
import {AuthenticationService} from '@app/core/service/authentication.service';

@Component({
    selector   : 'ngc-login',
    templateUrl: './login.component.html',
    styleUrls  : ['./login.component.scss'],
    animations : ngcAnimations
})
export class NgcLoginComponent implements OnInit, AfterViewInit, AfterContentInit, OnDestroy {

    loginForm: UntypedFormGroup;
    loginFormErrors: any;
    returnUrl: string;
    lastVisitedPage: string;
    supportUrl: string;
    subscriptions: Subscription[] = [];
    isCredentialsLoginView = true;
    isMFALoginView = false;
    isMFASetupView = false;
    isRecoveryCodeLoginView = false;
    MFAPhone: string;
    isMFASetupFlow = false;
    user: User;
    recoveryCodeLoginIsInProgress: boolean;
    lastCredentialsLoginPayload: {username: string; password?: string; mfaCode?: string;  resetCode?: string; errorMessage: string};

    /**
     * True if the authentication is loading$.
     * @type {boolean}
     */
    public loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    /**
     * The error if authentication fails.
     * @type {Observable<string>}
     */
    public error: Observable<string>;

    /**
     * Component state.
     * @type {boolean}
     */
    private alive = true;

    constructor(
        private authenticatedUserService: AuthenticatedUserService,
        private authenticationService: AuthenticationService,
        private formBuilder: UntypedFormBuilder,
        public dialog: MatDialog,
        public route: ActivatedRoute,
        public router: Router,
        private store: Store<State>,
        public snackBar: MatSnackBar,
        private themeService: ThemeService,
        private tokenService: TokenService,
        private viewContainerRef: ViewContainerRef,
        private oidcSecurityProxyService: OidcSecurityProxyService
    ) {
    }

    ngOnInit() {
        this.initFormGroup();

        if (this.authenticatedUserService.isAuthenticated()) {
            this.router.navigate(['']);
        } else {
            // set loading$
            this.loading$.next(false);

            this.lastVisitedPage = this.route.snapshot.queryParams['lastVisitedPage'];
            this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
            if (this.returnUrl) {
                this.returnUrl = (isLoginRoute(this.returnUrl) ? '/' : this.returnUrl);
            }

            this.subscriptions.push(
                this.themeService.getBundle().subscribe(bundle => {
                    this.supportUrl = (bundle && bundle['footer'] && bundle['footer']['supportLink'] && bundle['footer']['supportLink']['url']
                        ? bundle['footer']['supportLink']['url'] : null);
                })
            );
        }
    }

    private initFormGroup() {
        this.loginFormErrors = {
            username: {},
            password: {},
            verificationCode: {},
            recoveryCode: {}
        };

        this.loginForm = this.formBuilder.group({
            username: ['', [Validators.required]],
            password: ['', Validators.required],
            verificationCode: [''],
            recoveryCode: ['']
        });
    }

    ngAfterContentInit() {

    }

    ngAfterViewInit(): void {
        if (this.loginForm) {
            this.subscriptions.push(
                this.loginForm.valueChanges
                    .subscribe(() => {
                        this.onLoginFormValuesChanged();
                    })
            );

            this.subscriptions.push(
                this.loginForm.controls.username.valueChanges
                    .subscribe(val => {
                        if (val) {
                            const trimmedValue = val.toString().trim();
                            if (trimmedValue != val) {
                                this.loginForm.controls.username.patchValue(trimmedValue, {emitEvent: false});
                            }
                        }
                    })
            );
        }


        // subscribe to success
        this.subscriptions.push(
            this.store.pipe(
                select(getAuthenticatedUser),
                takeWhile(() => this.alive),
                filter(user => !!user)
            )
                .subscribe((user: User) => {
                    this.user = user;

                    if (user?.needToSetupMFA) {
                        // Setup MFA for Vendor
                        this.onTurnOnMFASetupView();
                        this.isMFASetupFlow = true;
                    } else {

                        this.loading$.next(false);

                        if (user.orgGuid) {
                            if (this.isCredentialsLoginView || this.isMFALoginView || this.isRecoveryCodeLoginView) {
                               this.performOrdinaryLogin(user);
                            }
                        } else {

                            if (user && user.mfaPhone) {
                                // mfa login
                                this.MFAPhone = '********' + ((user && user.mfaPhone) ? user.mfaPhone.substr(-3, 3) : '');

                                if (this.recoveryCodeLoginIsInProgress) {
                                    // recovery code login
                                    this.onTurnOnRecoveryCodeLoginView();
                                } else {
                                    // ordinary mfa login
                                    this.onTurnOnMFALoginView();
                                }

                            } else {
                                this.onTurnOnCredentialsLoginView();
                            }
                        }
                    }
                })
        );

        // // redirect Provider to 'admin/applications'
        // this.authenticatedUserService.authenticatedUser$
        //     .subscribe( user => {
        //         if (this.authenticatedUserService.isProvider(user)) {
        //             this.router.navigate(['admin/applications']);
        //         }
        //     });

        // subscribe to fails
        this.store.pipe(
            select(getAuthenticationError),
            takeWhile(() => this.alive),
            filter(m => m && m.length > 0)
        )
            .subscribe(message => {
                if (message) {
                    this.loading$.next(false);

                    if ((message === UserStatus.PASSWORD_RESET_REQUIRED) || (message === UserStatus.TO_BE_DELETED)) {
                        this.openUserStatusIssueDialog(message);
                    } else if (message === UserStatus.ETX_LOGIN_REQUIRED) {
                        this.openDistrictAdminLoginBlockedDialog(message);
                    } else {
                        this.openSnackBar(<string>message, 'Authentication');
                    }

                    if (this.isRecoveryCodeLoginView) {
                        this.recoveryCodeLoginIsInProgress = true;
                    }

                    if (this.isMFALoginView || this.isRecoveryCodeLoginView) {
                        this.loginForm.controls.verificationCode.setValue('');
                        this.loginForm.controls.verificationCode.markAsPristine();
                    }

                    this.store.dispatch(new authAction.AuthenticationErrorAction({error: {message: ''}}));
                }
            });
    }

    performOrdinaryLogin(user: User) {
        if (this.isMFASetupFlow) {
            this.router.navigate(['/login/vendor']);
        } else {
            if (this.lastVisitedPage && user.username.toLocaleLowerCase() === localStorage.getItem('lastAuthenticatedUser')) {
                this.router.navigate([this.lastVisitedPage]);
            } else {
                this.router.navigate([this.returnUrl || '']);
            }
        }
    }

    onLoginFormValuesChanged() {
        for ( const field in this.loginFormErrors ) {
            if ( !this.loginFormErrors.hasOwnProperty(field) ) {
                continue;
            }

            // Clear previous errors
            this.loginFormErrors[field] = {};

            // Get the control
            const control = this.loginForm.get(field);

            if ( control && control.dirty && !control.valid ) {
                this.loginFormErrors[field] = control.errors;
            }
        }
    }

    onFormSubmit(formValue: any) {
        if (this.loginForm.invalid) {
            return;
        }

        let payload: {username: string; password?: string; mfaCode?: string;  resetCode?: string; errorMessage: string};
        if (this.isCredentialsLoginView) {
            payload = {
                username: formValue.username,
                password: formValue.password,
                errorMessage: 'Incorrect email or password'
            };
            this.lastCredentialsLoginPayload = payload;
        }
        if (this.isMFALoginView) {
            payload = {
                username: this.user.username,
                mfaCode: formValue.verificationCode,
                errorMessage: 'Incorrect code'
            };
        }
        if (this.isRecoveryCodeLoginView) {
            payload = {
                username: this.user.username,
                resetCode: formValue.recoveryCode,
                errorMessage: 'Incorrect recovery code'
            };
        }
        this.performLogin(payload);
    }

    performLogin(
        payload: { username: string; password?: string; mfaCode?: string; resetCode?: string; errorMessage: string } = this.lastCredentialsLoginPayload
    ) {
        this.loading$.next(true);
        this.store.dispatch(new authAction.AuthenticateAction(payload));
    }

    openSnackBar(message: string, action: string) {
        this.snackBar.open(message, action, <MatSnackBarConfig>{
            duration: 3000,
        });
    }

    private openUserStatusIssueDialog(userStatus: UserStatus) {
        const config = new MatDialogConfig();
        config.viewContainerRef = this.viewContainerRef;
        config.disableClose = true;
        config.autoFocus = true;

        const dialogRef = this.dialog.open(ConfirmDialogComponent, config);
        if (userStatus === UserStatus.TO_BE_DELETED) {
            dialogRef.componentInstance.item = 'other';
            dialogRef.componentInstance.otherItemCancelBtnTitle = 'Cancel';
            dialogRef.componentInstance.otherItemConfirmBtnTitle = 'Contact Support';
            dialogRef.componentInstance.header = 'Account blocked';
            dialogRef.componentInstance.target = 'Your account has been blocked due to long innactivity. Please contact support for assistance.';

            this.subscriptions.push(
                dialogRef.afterClosed()
                    .subscribe(result => {
                        if (result === 'ok') {
                            this.subscriptions.push(
                                this.themeService.getBundle()
                                    .subscribe(bundle =>
                                        window.location.href = this.themeService.getValueOfTarget(bundle, 'footer.supportLink.url')
                                    )
                            );
                        }
                    })
            );
        } else {
            dialogRef.componentInstance.item = 'error';
            dialogRef.componentInstance.header = 'Update your password';
            dialogRef.componentInstance.target = `Your password has expired and needs to be updated. An email with a link to reset your password has been sent to your registered email address. Please follow the link to set a new password.`;
        }
    }

    private openDistrictAdminLoginBlockedDialog(userStatus: UserStatus) {
        const config = new MatDialogConfig();
        config.viewContainerRef = this.viewContainerRef;
        config.disableClose = true;
        config.autoFocus = true;

        const dialogRef = this.dialog.open(ConfirmDialogComponent, config);
        dialogRef.componentInstance.item = 'error';
        dialogRef.componentInstance.header = 'Invalid user role';
        dialogRef.componentInstance.target = 'Please, use District Admin login form to login. Click Ok to proceed.';

        this.subscriptions.push(
            dialogRef.afterClosed()
                .subscribe(result => {
                    if (result === 'ok') {
                        // this.oidcSecurityProxyService.authorize();
                        window.location.href = environment.etxBaseURI;
                    }
                })
        );
    }

    onBackToCredentialsLogIn() {
        this.onTurnOnCredentialsLoginView();
        const updatedUser: User = copyData(this.user);
        updatedUser.mfaPhone = null;
        updatedUser.needToSetupMFA = null;
        this.tokenService.removeToken();
        this.store.dispatch(new authAction.AuthenticatedUserRefreshSuccessAction({ user: updatedUser}));
    }

    onBackToMFALogIn() {
        this.onTurnOnMFALoginView();
    }

    onSendCodeAgain() {
        this.store.dispatch(new authAction.SendCodeAgainAction());
    }

    private turnOffCredentialsLoginView() {
        this.isCredentialsLoginView = false;
        this.loginForm.controls.username.clearValidators();
        this.loginForm.controls.password.clearValidators();
        this.loginForm.controls.username.reset();
        this.loginForm.controls.password.reset();
        this.loginForm.controls.username.updateValueAndValidity();
        this.loginForm.controls.password.updateValueAndValidity();
    }

    onTurnOnCredentialsLoginView() {
        this.turnOnCredentialsLoginView();
        this.turnOffMFALoginView();
        this.turnOffMFASetupView();
        this.turnOffRecoveryCodeLoginView();
    }

    private turnOnCredentialsLoginView() {
        this.isCredentialsLoginView = true;
        this.loginForm.controls.username.setValidators(Validators.required);
        this.loginForm.controls.password.setValidators(Validators.required);
        this.loginForm.controls.verificationCode.clearValidators();
        this.loginForm.controls.verificationCode.reset();
        this.loginForm.controls.username.updateValueAndValidity();
        this.loginForm.controls.password.updateValueAndValidity();
    }

    private onTurnOnMFASetupView() {
        this.turnOffCredentialsLoginView();
        this.turnOffRecoveryCodeLoginView();
        this.turnOnMFASetupView();
        this.recoveryCodeLoginIsInProgress = false;
    }

    private turnOnMFASetupView() {
        this.isMFASetupView = true;
    }

    private turnOffMFASetupView() {
        this.isMFASetupView = false;
        this.isMFASetupFlow = false;
    }

    private turnOffMFALoginView() {
        this.isMFALoginView = false;
        this.loginForm.controls.verificationCode.clearValidators();
        this.loginForm.controls.verificationCode.reset();
        this.loginForm.controls.verificationCode.updateValueAndValidity();
    }

    private onTurnOnMFALoginView() {
        this.turnOnMFALoginView();
        this.turnOffCredentialsLoginView();
        this.turnOffRecoveryCodeLoginView();
        this.recoveryCodeLoginIsInProgress = false;
    }

    private turnOnMFALoginView() {
        this.isMFALoginView = true;
        this.loginForm.controls.verificationCode.setValidators([
            Validators.required,
            Validators.minLength(4),
            Validators.maxLength(6),
            Validators.pattern(/^[0-9]*$/)
        ]);
        this.loginForm.controls.verificationCode.updateValueAndValidity();
    }

    private turnOffRecoveryCodeLoginView() {
        this.isRecoveryCodeLoginView = false;
        this.loginForm.controls.recoveryCode.clearValidators();
        this.loginForm.controls.recoveryCode.reset();
        this.loginForm.controls.recoveryCode.updateValueAndValidity();
    }

    onTurnOnRecoveryCodeLoginView() {
        this.turnOnRecoveryCodeLoginView();
        this.turnOffCredentialsLoginView();
        this.turnOffMFALoginView();

    }

    private turnOnRecoveryCodeLoginView() {
        this.isRecoveryCodeLoginView = true;
        this.loginForm.controls.recoveryCode.setValidators([
            Validators.required,
            Validators.minLength(10),
            Validators.maxLength(10)
        ]);
        this.loginForm.controls.recoveryCode.updateValueAndValidity();
    }

    public ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.alive = false;
    }
}
